import {
  blueGreyMui,
  deepPurpleMui,
  greyMui,
  indigoMui,
  lightBlueMui,
  orangeMui,
  tealMui,
} from "@ryde/theme";

import type { RydeUiUnit } from "../models/common.models";
import { DrawerType } from "../models/drawers.models";

export const DEFAULT_HISTORY_ACCENT = greyMui._500;

/** Production-related drawers (purple). */
export const PRODUCTION_HISTORY_DRAWERS: ReadonlySet<DrawerType> = new Set([
  DrawerType.BookingsProduction,
  DrawerType.CoursesCalendarRideProduce,
  DrawerType.CustomProduction,
  DrawerType.ProductionPreview,
  DrawerType.ProductionViewLogs,
]);

export const RIDE_HISTORY_ENTITIES: ReadonlySet<RydeUiUnit> = new Set([
  "ride",
  "rideMap",
  "rideType",
]);

export const PRODUCTION_HISTORY_ENTITIES: ReadonlySet<RydeUiUnit> = new Set([
  "rideProduction",
]);

export const RIDE_HISTORY_DRAWERS: ReadonlySet<DrawerType> = new Set([
  DrawerType.RideEditForm,
  DrawerType.RideHistoryLog,
]);

export const BOOKING_HISTORY_DRAWERS: ReadonlySet<DrawerType> = new Set([
  DrawerType.BookingEditForm,
  DrawerType.BulkCreateBookings,
  DrawerType.BulkUploadBookings,
  DrawerType.BulkUpdateBookingsDateTime,
  DrawerType.AddNewBulkBookings,
]);

export const USER_ACTIONS_HISTORY_EXCLUDED_DRAWER_TYPES: ReadonlySet<DrawerType> =
  new Set([
    DrawerType.AddNewBulkBookings,
    DrawerType.AdministrativeMode,
    DrawerType.AiConversationDetails,
    DrawerType.HubStationsInfo,
    DrawerType.CourseSchedulesPreview,
    DrawerType.BookingsProduction,
    DrawerType.CoursesCalendarRideProduce,
    DrawerType.BulkActivateShiftTemplate,
    DrawerType.BulkCreateBookings,
    DrawerType.BulkDriversMessage,
    DrawerType.BulkPassengerNearBySearch,
    DrawerType.BulkPassengersMessage,
    DrawerType.BulkUploadBookings,
    DrawerType.BulkUpdateBookingsDateTime,
    DrawerType.CopyToSupplierPriceRates,
    DrawerType.CustomProduction,
    DrawerType.EtlViewLogs,
    DrawerType.GenericExport,
    DrawerType.GenericUpload,
    DrawerType.HistoryLog,
    DrawerType.ImportAreas,
    DrawerType.ImportBusStations,
    DrawerType.JourneyDuplicate,
    DrawerType.Notifications,
    DrawerType.PriceRateSearchLogs,
    DrawerType.ProcessDrawer,
    DrawerType.ProductionPreview,
    DrawerType.ProductionViewLogs,
    DrawerType.RideHistoryLog,
    DrawerType.SupplierPriceRatesPassword,
    DrawerType.UpdateBasePrice,
    DrawerType.UserActionsHistory,
    DrawerType.RelatedEntities,
    DrawerType.EditAreaWaypoints,
  ]);

/**
 * Drawer types whose props omit `entity` in JSON but map to one UI unit for labels (`*.single`).
 */
export const DRAWER_TYPE_IMPLICIT_ENTITY: Partial<
  Record<DrawerType, RydeUiUnit>
> = {
  [DrawerType.RideEditForm]: "ride",
  [DrawerType.JourneyEditForm]: "journey",
  [DrawerType.JourneyDuplicate]: "journey",
  [DrawerType.BookingEditForm]: "booking",
  [DrawerType.CourseEditForm]: "course",
  [DrawerType.CoursesCalendarRideProduce]: "course",
  [DrawerType.StationRouteEditForm]: "stationRoute",
  [DrawerType.NotificationEditForm]: "notification",
  [DrawerType.BulkCreateBookings]: "booking",
  [DrawerType.BulkUploadBookings]: "booking",
  [DrawerType.BulkUpdateBookingsDateTime]: "booking",
  [DrawerType.AddNewBulkBookings]: "booking",
  [DrawerType.BookingsProduction]: "rideProduction",
  [DrawerType.CustomProduction]: "rideProduction",
  [DrawerType.ProductionPreview]: "rideProduction",
  [DrawerType.ProductionViewLogs]: "rideProduction",
};

export const DRAWER_TYPE_TITLE_I18N_KEY: Partial<Record<DrawerType, string>> = {
  [DrawerType.Notifications]: "notification.plural",
};

/** Extra dot colors for entities not covered by ride/booking/course/station rules. */
export const ENTITY_PLAQUE_ACCENT: Partial<Record<RydeUiUnit, string>> = {
  journey: tealMui._600,
  passenger: lightBlueMui._600,
  notification: deepPurpleMui._500,
  driver: orangeMui._600,
  vehicle: indigoMui._500,
  supplier: blueGreyMui._600,
  site: tealMui._400,
  station: lightBlueMui._700,
};

/** Edit-like drawers that carry a numeric record id (or copy-from id) for route-card history. */
export const CRUD_ROUTE_ENTITY_DRAWER_TYPES: ReadonlySet<DrawerType> = new Set([
  DrawerType.EditForm,
  DrawerType.RideEditForm,
  DrawerType.BookingEditForm,
  DrawerType.CourseEditForm,
  DrawerType.JourneyEditForm,
  DrawerType.StationRouteEditForm,
  DrawerType.NotificationEditForm,
]);
